// File: src/components/MatchFlow/OrbitMatchCTA.jsx
import React, { useState, useMemo } from 'react';
import { FaSpinner } from 'react-icons/fa';
import DatingFilterBottomSheet from './DatingFilterBottomSheet';

// Geometry
const BIG = 280;
const SMALL = Math.round(BIG * 0.7);
const AVATAR = 40;
const CENTER = 90;

const RING_STROKE = 2;
const bigR = BIG / 2;
const smallR = SMALL / 2;

// Align avatar center exactly on the ring centerline:
// distance from orbit-center to avatar-center = (ringRadius - stroke/2)
const bigTranslate = (bigR - RING_STROKE / 2) - (AVATAR / 2);
const smallTranslate = (smallR - RING_STROKE / 2) - (AVATAR / 2);

// Helper for positioning avatars on ring
function pos(angleDeg, dist) {
  return `rotate(${angleDeg}deg) translate(${dist}px) rotate(-${angleDeg}deg)`;
}

export default function OrbitMatchCTA({
  isFinding,
  profileUrl,
  onToggle,
  userId,
  eventId,
  purposeTag,
}) {
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const isDating = (purposeTag || '').toLowerCase() === 'dating';

  const ctaLabel = isFinding ? 'Searching…' : 'Find Match';

  const motion = useMemo(
    () => ({
      orbitDurationBig: isFinding ? '38s' : '70s',
      orbitDurationSmall: isFinding ? '28s' : '52s',
      orbitState: isFinding ? 'running' : 'paused',
    }),
    [isFinding]
  );

  return (
    <>
      {/* Local animations + consistency tweaks */}
      <style>{`
        @keyframes orbitSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes subtleBeat { 0% { transform: scale(1); } 50% { transform: scale(1.035); } 100% { transform: scale(1); } }
        @keyframes ripple { 0% { transform: translate(-50%, -50%) scale(0.6); opacity: .45; } 100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ✅ actually spin the spinner */
        .spin { animation: spin 1s linear infinite; }

        @media (prefers-reduced-motion: reduce) {
          .orbit-anim { animation: none !important; }
          .spin { animation: none !important; }
          .ripple { animation: none !important; opacity: .15 !important; transform: translate(-50%, -50%) scale(1) !important; }
        }
      `}</style>

      <section style={S.card}>
        {/* Soft backdrop */}
        <div style={S.cardBackdrop} />

        {/* Orbit Stage */}
        <div style={S.stage}>
          {/* Guide rings (SVG for crisp strokes) */}
          <svg
            width={BIG}
            height={BIG}
            viewBox={`0 0 ${BIG} ${BIG}`}
            style={S.ringsSvg}
            aria-hidden
          >
            <defs>
              <linearGradient id="ring" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ddd" />
                <stop offset="100%" stopColor="#ddd" />
              </linearGradient>
            </defs>
            {/* Outer ring */}
            <circle
  cx={bigR}
  cy={bigR}
  r={bigR - RING_STROKE}
  fill="none"
  stroke="url(#ring)"
  strokeWidth={RING_STROKE}
  strokeDasharray="2 12"         // tighter dots, more air
  strokeLinecap="round"          // rounded dash ends = premium feel
  strokeOpacity=".9"
  shapeRendering="geometricPrecision"
/>

{/* Inner ring */}
<circle
  cx={bigR}
  cy={bigR}
  r={smallR - RING_STROKE}
  fill="none"
  stroke="url(#ring)"
  strokeWidth={RING_STROKE}
  strokeDasharray="2 14"
  strokeLinecap="round"
  strokeOpacity=".75"
  shapeRendering="geometricPrecision"
/>
          </svg>

          {/* Big ring orbiters (use 0° and 180° to sit exactly on ring) */}
          <div
            className="orbit-anim"
            style={{
              ...S.orbitCenter,
              animation: `orbitSpin ${motion.orbitDurationBig} linear infinite`,
              animationPlayState: motion.orbitState,
            }}
          >
            <img
              src="https://i.pravatar.cc/36?img=31"
              alt=""
              style={{ ...S.avatar, transform: pos(0, bigTranslate) }}
            />
            <img
              src="https://i.pravatar.cc/36?img=32"
              alt=""
              style={{ ...S.avatar, transform: pos(180, bigTranslate) }}
            />
          </div>

          {/* Small ring orbiter (90° to sit on top of inner ring) */}
          <div
            className="orbit-anim"
            style={{
              ...S.orbitCenter,
              animation: `orbitSpin ${motion.orbitDurationSmall} linear infinite reverse`,
              animationPlayState: motion.orbitState,
            }}
          >
            <img
              src="https://i.pravatar.cc/36?img=33"
              alt=""
              style={{ ...S.avatar, transform: pos(90, smallTranslate) }}
            />
          </div>

          {/* Center pod */}
          <div style={S.centerPod}>
            {isFinding && (
              <>
                <span className="ripple" style={{ ...S.ripple, animationDelay: '0s' }} />
                <span className="ripple" style={{ ...S.ripple, animationDelay: '.7s' }} />
                <span className="ripple" style={{ ...S.ripple, animationDelay: '1.4s' }} />
              </>
            )}
            <div style={S.profileFrame}>
              <img
                src={profileUrl || '/default-avatar.png'}
                alt="Your profile"
                style={{
                  ...S.profileImg,
                  ...(isFinding ? { animation: 'subtleBeat 2.4s ease-in-out infinite' } : {}),
                }}
              />
            </div>
          </div>
        </div>

        {/* COPY */}
        <h3 style={S.headline}>Ready to Connect?</h3>
        <p style={S.subcopy}>
          We’ll pair you with someone in the room who matches your preferences.
        </p>

        {/* CTA */}
        <div style={S.ctaStack} aria-live="polite">
          <button
            onClick={onToggle}
            style={{ ...S.primaryBtn, ...(isFinding ? S.primaryBtnActive : {}) }}
          >
            {isFinding && <FaSpinner className="spin" style={S.spinner} />}
            <span style={{ marginLeft: isFinding ? 8 : 0 }}>{ctaLabel}</span>
          </button>
        </div>
      </section>

      {isDating && (
        <DatingFilterBottomSheet
          userId={userId}
          eventId={eventId}
          open={showFilterSheet}
          onClose={() => setShowFilterSheet(false)}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Styles  ───────────────────────── */
const S = {
  card: {
    position: 'relative',
    margin: '0rem clamp(0px,5vw,40px) 2rem',
    padding: '2.25rem 1.25rem 1.5rem',
    borderRadius: 14,
    background: '#fff',
   
    overflow: 'hidden',
    textAlign: 'center',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    color: '#0f172a',
    
  },
  cardBackdrop: {
    position: 'absolute',
    inset: 0,

    pointerEvents: 'none',
  },

  stage: {
    position: 'relative',
    width: BIG,
    height: BIG,
    margin: '0 auto 0.75rem', // slightly more breathing room
  },
  ringsSvg: {
    display: 'block',
    margin: '0 auto',
    filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.02))',
  },
  orbitCenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 1,
    height: 1,
    transform: 'translate(-50%, -50%)',
    transformOrigin: 'center center',
  },
  avatar: {
    position: 'absolute',
    width: AVATAR,
    height: AVATAR,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    background: '#fff',
  },

  centerPod: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: CENTER,
    height: CENTER,
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
  },
  profileFrame: {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    overflow: 'hidden',
    background: '#fff',
    border: '3px solid #fff',
    boxShadow:
      '0 6px 20px rgba(30, 97, 240, 0.15), inset 0 0 0 1px rgba(0,0,0,0.04)',
  },
  profileImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  ripple: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: CENTER,
    height: CENTER,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(59,130,246,.35) 35%, rgba(59,130,246,0) 70%)',
    transform: 'translate(-50%, -50%)',
    animation: 'ripple 2.4s ease-out infinite',
    pointerEvents: 'none',
  },

  headline: {
    margin: '2rem 0 0.35rem',
    fontWeight: 800,
    fontSize: '1.25rem',
    color: '#0F172A',
    letterSpacing: '.2px',
  },
  subcopy: {
    margin: '0 0 1.1rem',
    fontSize: '.9rem',
    color: '#475569',
  },

  ctaStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 280,
    height: 40,
    padding: '0 14px',
    border: '1px solid transparent',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 13,
    color: '#fff',
    background: '#000',
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, opacity .12s ease',
  },
  primaryBtnActive: {
    transform: 'scale(1.015)',
    opacity: 0.96,
  },
  spinner: {
    fontSize: 18,
    marginTop: 1,
    marginLeft: 2,
  },

  secondaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 38,
    padding: '0 16px',
    minWidth: 280,
    borderRadius: 10,
    border: '1px solid #D8E3F6',
    background: '#fff',
    color: '#0F172A',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, border-color .12s ease',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
  },
};
